import { Check, Lock, Loader2, AlertCircle, Sparkles } from "lucide-react";
import { useSellerSetup } from "../context/SellerSetupContext";
import { useState } from "react";
import { toast } from "sonner";

/*
 * SellerSetupChecklist — onboarding banner rendered at the top of the
 * seller's /seller/settings page until they've finished their initial setup.
 *
 * Shows the 5 required checklist items (each with a click-to-jump-to-tab
 * action). When all 5 items are satisfied, the bottom CTA flips from grey
 * "Finish all 5 steps first" to an active "Complete setup & unlock portal"
 * button. Hitting it calls POST /seller/setup-status/complete which stamps
 * setup_completed_at and unlocks the rest of the portal.
 *
 * Once the seller has completed setup, this component renders nothing.
 */
export default function SellerSetupChecklist({ onJumpToTab }) {
  const { status, loading, refresh, complete } = useSellerSetup();
  const [submitting, setSubmitting] = useState(false);

  if (loading && !status) {
    return (
      <div className="mx-8 my-4 px-5 py-4 rounded-2xl border border-border bg-secondary/30 flex items-center gap-3 text-foreground/55 text-sm">
        <Loader2 className="w-4 h-4 animate-spin"/>
        Loading your setup status…
      </div>
    );
  }

  // Already complete → render nothing. The portal is unlocked.
  if (!status || status.complete) return null;

  const handleComplete = async () => {
    setSubmitting(true);
    try {
      await complete();
      toast.success("Setup complete — your portal is now unlocked.", { duration: 6000 });
      // Refresh again so PortalShell's sidebar re-renders without locks.
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === "object" && detail?.message)
        ? detail.message
        : (typeof detail === "string" ? detail : "Couldn't complete setup. Try again.");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const pct = Math.round((status.completed_steps / status.total_steps) * 100);
  const allDone = status.can_complete;

  return (
    <div className="mx-8 mt-4" data-testid="seller-setup-checklist">
      <div className="rounded-2xl border-2 border-accent/40 bg-accent/[0.06] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-accent/30 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-foreground text-accent grid place-items-center shrink-0">
              <Sparkles className="w-4 h-4"/>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] mono uppercase tracking-widest text-foreground/55">Welcome — let's get you ready</div>
              <div className="display text-lg tracking-tight font-semibold">Finish your setup to start taking orders</div>
              <div className="text-[12.5px] text-foreground/65 mt-1 max-w-2xl">
                Your portal is locked while these settings are blank. Buyers can't place
                clean POs against a half-configured seller — lead times, warehouses, and
                terms anchor every doc we generate for you.
              </div>
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-2xl font-semibold tabular-nums" data-testid="setup-progress-count">
              {status.completed_steps}<span className="text-foreground/40">/{status.total_steps}</span>
            </div>
            <div className="text-[10px] mono uppercase tracking-widest text-foreground/50">complete</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="px-6 pt-4">
          <div className="h-2 w-full rounded-full bg-foreground/10 overflow-hidden">
            <div
              className="h-full bg-accent transition-all"
              style={{ width: `${pct}%` }}
              data-testid="setup-progress-bar"
            />
          </div>
        </div>

        {/* Checklist */}
        <ul className="px-3 py-3 divide-y divide-border/60">
          {status.steps.map(s => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onJumpToTab?.(s.tab)}
                data-testid={`setup-step-${s.id}`}
                className={`w-full text-left px-3 py-3 rounded-lg flex items-start gap-3 transition-colors ${
                  s.complete ? "hover:bg-foreground/[0.03]" : "hover:bg-accent/15"
                }`}
              >
                <span
                  className={`shrink-0 w-6 h-6 rounded-full grid place-items-center transition-colors ${
                    s.complete
                      ? "bg-foreground text-accent"
                      : "bg-white border-2 border-foreground/25 text-foreground/40"
                  }`}
                  data-testid={`setup-step-icon-${s.id}`}
                >
                  {s.complete ? <Check className="w-3.5 h-3.5"/> : <Lock className="w-3 h-3"/>}
                </span>
                <div className="flex-1 min-w-0">
                  <div className={`text-[13.5px] font-medium ${s.complete ? "text-foreground/55 line-through" : "text-foreground"}`}>
                    {s.label}
                  </div>
                  <div className="text-[12px] text-foreground/55 mt-0.5 leading-snug">{s.description}</div>
                </div>
                {!s.complete && (
                  <span className="text-[10.5px] mono uppercase tracking-widest text-foreground/55 mt-1">
                    Open →
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>

        {/* CTA */}
        <div className="px-6 py-4 border-t border-accent/30 bg-foreground/[0.03] flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-[12px] text-foreground/65">
            {allDone ? (
              <>
                <Check className="w-4 h-4 text-foreground"/>
                <span>All set. Click below to open your portal.</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-4 h-4 text-foreground/55"/>
                <span>{status.total_steps - status.completed_steps} item{status.total_steps - status.completed_steps === 1 ? "" : "s"} remaining</span>
              </>
            )}
          </div>
          <button
            type="button"
            disabled={!allDone || submitting}
            onClick={handleComplete}
            data-testid="setup-complete-btn"
            className={`text-[13px] font-medium rounded-full px-5 py-2.5 transition-colors ${
              allDone && !submitting
                ? "bg-foreground text-accent hover:opacity-90"
                : "bg-foreground/10 text-foreground/45 cursor-not-allowed"
            }`}
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin"/>Unlocking…</span>
            ) : allDone ? (
              "Complete setup & unlock portal"
            ) : (
              `Finish ${status.total_steps - status.completed_steps} more step${status.total_steps - status.completed_steps === 1 ? "" : "s"}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
